import { patientApi } from '@/api/PatientApi'
import { CHARGE_NOT_PAID } from '@/api/types/PatientApi'
import type { PatientBillingCharge } from '@/api/types/PatientApi'
import { isApiRequestError } from '@/errors/ApiRequestError'
import { logger } from '@/lib/logger'
import { useMutation } from '@tanstack/react-query'
import * as FileSystem from 'expo-file-system'
import { useCallback } from 'react'
import { Platform, Share } from 'react-native'
import Toast from 'react-native-toast-message'

const BASE64_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/'

/** Raw bytes to base64, three bytes at a time, so a PDF of any size never touches the call stack. */
const arrayBufferToBase64 = (buffer: ArrayBuffer) => {
	const bytes = new Uint8Array(buffer)
	const len = bytes.length
	let base64 = ''
	for (let i = 0; i < len; i += 3) {
		const b0 = bytes[i] ?? 0
		const b1 = bytes[i + 1] ?? 0
		const b2 = bytes[i + 2] ?? 0
		base64 += BASE64_CHARS.charAt(b0 >> 2)
		base64 += BASE64_CHARS.charAt(((b0 & 3) << 4) | (b1 >> 4))
		base64 += i + 1 < len ? BASE64_CHARS.charAt(((b1 & 15) << 2) | (b2 >> 6)) : '='
		base64 += i + 2 < len ? BASE64_CHARS.charAt(b2 & 63) : '='
	}
	return base64
}

const receiptUri = (paymentId: number) =>
	`${FileSystem.cacheDirectory}WallyReceipt${paymentId}.pdf`

/**
 * A charge's PDF receipt, cached to disk under its own name so a second tap on the same charge
 * never re-downloads it. `download` fetches (or reuses) the file and hands it to the system
 * share sheet, where it can be saved to Files, sent on or printed; a failure surfaces as a
 * toast rather than a broken screen.
 */
const useChargeReceipt = () => {
	const mutation = useMutation({
		mutationFn: async (charge: PatientBillingCharge) => {
			const uri = receiptUri(charge.paymentId)
			const info = await FileSystem.getInfoAsync(uri)
			if (info.exists) return uri

			const buffer = await patientApi.getPaymentReceipt(charge.paymentId)
			await FileSystem.writeAsStringAsync(uri, arrayBufferToBase64(buffer), {
				encoding: FileSystem.EncodingType.Base64,
			})
			return uri
		},
		onSuccess: (uri) => {
			if (Platform.OS === 'ios') Share.share({ url: uri })
			else Share.share({ message: uri })
		},
		onError: (error) => {
			logger.error(new Error('failed_to_get_charge_receipt', { cause: error }))
			const notPaid = isApiRequestError(error) && error.code === CHARGE_NOT_PAID
			Toast.show({
				type: 'error',
				text1: notPaid ? 'No receipt for this payment' : "Couldn't load the receipt",
			})
		},
	})

	const { mutate } = mutation
	const download = useCallback((charge: PatientBillingCharge) => mutate(charge), [mutate])

	return {
		download,
		isLoading: mutation.isPending,
		error: mutation.error,
	}
}

export { useChargeReceipt }
